/**
 * <headless-mailing> — a headless, framework-agnostic mailing/registration form.
 *
 * Sibling of <headless-shop> and <headless-tryon>: configured by attributes,
 * themed entirely with CSS custom properties (`--hm-*`), no framework runtime,
 * no opinion about your brand. It renders an email field (plus optional name /
 * message fields), guards against bots with an off-canvas honeypot, and POSTs
 * one JSON body to your relay endpoint (for example the included Cloudflare
 * Worker that submits directly to authenticated Stalwart SMTP).
 *
 * Attributes:
 *   endpoint            absolute URL of the relay's POST endpoint.
 *                       Empty → preview mode (submit shows `preview-message`).
 *   heading             optional small uppercase heading above the form.
 *   cta-label           submit button label. Default "Subscribe".
 *   show-name           "on" (default) | "off" — the optional name field.
 *   show-message        "on" | "off" (default) — the optional message textarea.
 *   name-label / email-label / message-label            field labels.
 *   name-placeholder / email-placeholder / message-placeholder
 *   note                small print under the button.
 *   success-message     shown after a successful POST.
 *                       Default "Thank you. You are on the list."
 *   error-message       shown when the POST fails.
 *   preview-message     shown on submit while `endpoint` is unset.
 *   honeypot-name       name of the honeypot field. Default "website_url".
 *   payload             JSON object merged into the POST body (e.g. '{"source":"landing"}').
 *
 * POST body: { email, name?, message?, [honeypot-name]: "" , ...payload }
 * Expected response: { ok: true } — anything else is treated as an error.
 *
 * Events: `headless-mailing:success` (detail {payload})
 *         `headless-mailing:error`   (detail {error, status?})
 */
import type { MailingPayload } from './types'
import { STYLES } from './styles'

type Phase = 'idle' | 'sending' | 'done' | 'error' | 'preview'

export class HeadlessMailing extends HTMLElement {
  private root: ShadowRoot
  private phase: Phase = 'idle'
  private errorText = ''

  constructor() {
    super()
    this.root = this.attachShadow({ mode: 'open' })
  }

  connectedCallback(): void {
    this.render()
  }

  // ---- attributes ---------------------------------------------------------
  private attr(name: string, fallback = ''): string {
    const v = this.getAttribute(name)
    return v == null || v === '' ? fallback : v
  }
  private get endpoint(): string {
    return this.attr('endpoint', '')
  }
  private get honeypotName(): string {
    return this.attr('honeypot-name', 'website_url')
  }
  private get showName(): boolean {
    return this.attr('show-name', 'on') !== 'off'
  }
  private get showMessage(): boolean {
    return this.attr('show-message', 'off') === 'on'
  }
  private parsePayload(): Record<string, unknown> {
    const raw = this.getAttribute('payload')
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }

  // ---- submit -------------------------------------------------------------
  private async onSubmit(e: Event): Promise<void> {
    e.preventDefault()
    if (this.phase === 'sending') return

    const form = e.currentTarget as HTMLFormElement
    if (!form.reportValidity()) return
    const data = new FormData(form)
    const email = String(data.get('email') ?? '').trim()

    if (!this.endpoint) {
      this.phase = 'preview'
      this.render()
      return
    }

    const payload: MailingPayload = {
      email,
      [this.honeypotName]: String(data.get(this.honeypotName) ?? ''),
      ...this.parsePayload(),
    }
    const name = String(data.get('name') ?? '').trim()
    const message = String(data.get('message') ?? '').trim()
    if (name) payload.name = name
    if (message) payload.message = message

    this.phase = 'sending'
    this.render(payload)

    try {
      const resp = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
      const body: unknown = await resp.json().catch(() => ({}))
      const ok = resp.ok && !!body && typeof body === 'object' && (body as { ok?: unknown }).ok === true
      if (ok) {
        this.phase = 'done'
        this.dispatchEvent(
          new CustomEvent('headless-mailing:success', { detail: { payload }, bubbles: true, composed: true }),
        )
      } else {
        const err = (body as { error?: unknown })?.error
        this.errorText = typeof err === 'string' ? err : `request_failed_${resp.status}`
        this.phase = 'error'
        this.dispatchEvent(
          new CustomEvent('headless-mailing:error', {
            detail: { error: this.errorText, status: resp.status },
            bubbles: true,
            composed: true,
          }),
        )
      }
    } catch {
      this.errorText = 'network_error'
      this.phase = 'error'
      this.dispatchEvent(
        new CustomEvent('headless-mailing:error', {
          detail: { error: this.errorText },
          bubbles: true,
          composed: true,
        }),
      )
    }
    this.render()
  }

  // ---- render -------------------------------------------------------------
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
  }

  private render(keep?: MailingPayload): void {
    if (this.phase === 'done') {
      this.root.innerHTML = `
        <style>${STYLES}</style>
        <div class="success" part="success" role="status">
          ${this.esc(this.attr('success-message', 'Thank you. You are on the list.'))}
        </div>`
      return
    }

    const heading = this.attr('heading')
    const noteText = this.attr('note')
    const sending = this.phase === 'sending'
    const nameVal = typeof keep?.name === 'string' ? keep.name : ''
    const emailVal = typeof keep?.email === 'string' ? keep.email : ''
    const msgVal = typeof keep?.message === 'string' ? keep.message : ''

    this.root.innerHTML = `
      <style>${STYLES}</style>
      <form part="form">
        ${heading ? `<p class="heading" part="heading">${this.esc(heading)}</p>` : ''}
        ${
          this.showName
            ? `<div class="field" part="field">
                 <label for="hm-name">${this.esc(this.attr('name-label', 'Name'))}</label>
                 <input id="hm-name" name="name" type="text" autocomplete="name" maxlength="120"
                   placeholder="${this.esc(this.attr('name-placeholder', 'Your name'))}" value="${this.esc(nameVal)}" />
               </div>`
            : ''
        }
        <div class="field" part="field">
          <label for="hm-email">${this.esc(this.attr('email-label', 'Email'))}</label>
          <input id="hm-email" name="email" type="email" required autocomplete="email" maxlength="254"
            placeholder="${this.esc(this.attr('email-placeholder', 'you@example.com'))}" value="${this.esc(emailVal)}" />
        </div>
        ${
          this.showMessage
            ? `<div class="field" part="field">
                 <label for="hm-message">${this.esc(this.attr('message-label', 'Message'))}</label>
                 <textarea id="hm-message" name="message" rows="3" maxlength="5000"
                   placeholder="${this.esc(this.attr('message-placeholder', ''))}">${this.esc(msgVal)}</textarea>
               </div>`
            : ''
        }
        <input class="hp" type="text" name="${this.esc(this.honeypotName)}" tabindex="-1"
          autocomplete="off" aria-hidden="true" />
        <button type="submit" part="button" ${sending ? 'disabled' : ''}>
          ${sending ? this.esc(this.attr('sending-label', 'Please wait…')) : this.esc(this.attr('cta-label', 'Subscribe'))}
        </button>
        ${
          this.phase === 'error'
            ? `<p class="error" part="error" role="alert">${this.esc(
                this.attr('error-message', 'Something interrupted your request. Please try again.'),
              )}</p>`
            : ''
        }
        ${
          this.phase === 'preview'
            ? `<p class="error" part="error" role="status">${this.esc(
                this.attr('preview-message', 'Sign-up opens soon.'),
              )}</p>`
            : ''
        }
        ${noteText ? `<p class="note" part="note">${this.esc(noteText)}</p>` : ''}
      </form>`

    this.root.querySelector('form')?.addEventListener('submit', (e) => void this.onSubmit(e))
  }
}

if (!customElements.get('headless-mailing')) {
  customElements.define('headless-mailing', HeadlessMailing)
}
